export const DEFAULT_ORG_TIMEZONE = 'Asia/Kolkata';

/** Named display presets for org-scoped dates. */
export const ORG_DATE_FORMATS = [
  'date',
  'datetime',
  'time',
  'long',
  'short',
  'iso-date',
] as const;

export type OrgDateFormat = (typeof ORG_DATE_FORMATS)[number];

export interface FormatOrgDateOptions {
  /** Named preset. Defaults to `'date'`. */
  format?: OrgDateFormat;
  /** BCP 47 locale. Default en-IN. */
  locale?: string;
  /** @deprecated Prefer `format: 'datetime'`. */
  withTime?: boolean;
}

function resolveFormat(options: FormatOrgDateOptions): OrgDateFormat {
  if (options.format) return options.format;
  if (options.withTime) return 'datetime';
  return 'date';
}

function intlOptionsFor(format: OrgDateFormat): Intl.DateTimeFormatOptions {
  switch (format) {
    case 'time':
      return { hour: '2-digit', minute: '2-digit', hour12: true };
    case 'datetime':
      return {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      };
    case 'long':
      return {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      };
    case 'short':
      return { day: '2-digit', month: '2-digit', year: 'numeric' };
    case 'iso-date':
      return { year: 'numeric', month: '2-digit', day: '2-digit' };
    case 'date':
    default:
      return { day: 'numeric', month: 'short', year: 'numeric' };
  }
}

/** Coerce API / DB values into a valid Date (ISO, epoch ms, Date). */
export function parseOrgDate(value: string | Date | number | null | undefined): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const raw = String(value).trim();
  if (!raw) return null;

  // Prefer numeric epoch strings
  if (/^\d{10,13}$/.test(raw)) {
    const n = Number(raw);
    const d = new Date(raw.length === 10 ? n * 1000 : n);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format an ISO/UTC timestamp in an IANA timezone.
 * Always passes `timeZone` so device/browser timezone is ignored.
 *
 * Default `'date'` matches procure-mobile `D MMM, YYYY` (e.g. `1 Jul, 2026`).
 */
export function formatOrgDate(
  value: string | Date | number | null | undefined,
  timeZone: string = DEFAULT_ORG_TIMEZONE,
  options: FormatOrgDateOptions | OrgDateFormat = {},
): string {
  const d = parseOrgDate(value);
  if (!d) return value == null || value === '' ? '' : String(value);

  const opts: FormatOrgDateOptions =
    typeof options === 'string' ? { format: options } : options;
  const format = resolveFormat(opts);
  const tz = timeZone?.trim() || DEFAULT_ORG_TIMEZONE;
  const locale = opts.locale ?? 'en-IN';

  try {
    if (format === 'iso-date') {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(d);
    }

    // Match mobile: "1 Jul, 2026" / "1 Jul, 2026, 4:30 pm"
    if (format === 'date' || format === 'datetime') {
      const datePart = new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(d);
      // en-IN often yields "1 Jul 2026" — insert comma before year
      const withComma = datePart.replace(/\s(\d{4})$/, ', $1');
      if (format === 'date') return withComma;
      const timePart = new Intl.DateTimeFormat(locale, {
        timeZone: tz,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).format(d);
      return `${withComma}, ${timePart}`;
    }

    return new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      ...intlOptionsFor(format),
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat(locale, {
      timeZone: DEFAULT_ORG_TIMEZONE,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
      .format(d)
      .replace(/\s(\d{4})$/, ', $1');
  }
}
